<template>
    <div>
        <!-- Loading -->
        <LoadingSpinner v-if="loading && !svgRendered" class="tw:py-24" />

        <!-- Loaded -->
        <div v-else-if="!loading && svgRendered" class="tw:space-y-4">
            <!-- Mapa -->
            <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-200 tw:p-4">
                <!-- SvgPanZoom para tipo_svg == 1 -->
                <div v-if="zona.tipo_svg == 1">
                    <SvgPanZoom
                        class="svgpanzoom-container"
                        :zoomEnabled="true"
                        :controlIconsEnabled="false"
                        :fit="true"
                        :center="true"
                        :mouseWheelZoomEnabled="false"
                        :zoomScaleSensitivity="2"
                        :dblClickZoomEnabled="false"
                        :preventMouseEventsDefault="false"
                        @svgpanzoom="registerSvgPanZoom"
                    >
                        <svg v-html="svg_loaded" />
                    </SvgPanZoom>
                    <div v-if="load_panzoom" class="tw:flex tw:justify-center tw:gap-2 tw:mt-2">
                        <Button size="sm" @click="zoomIn">+</Button>
                        <Button size="sm" @click="zoomOut">-</Button>
                    </div>
                </div>
                <!-- Leaflet para tipo_svg == 2 -->
                <LeafletComponent
                    v-else
                    :svgRendered="svgRendered"
                    :originalColor="originalColor"
                    @isPanning="(e) => isPanning = e"
                />

                <!-- Leyenda -->
                <SeatLegend :items="legendItems" class="tw:mt-3" />

                <!-- Acciones -->
                <div class="tw:flex tw:gap-3 tw:mt-4">
                    <Button @click="selectAllButacas">Seleccionar todas</Button>
                    <Button v-if="plazasSeleccionadas.length > 0" @click="clearAllButacas">Limpiar todas</Button>
                </div>

                <!-- Stats -->
                <div class="tw:grid tw:grid-cols-3 tw:gap-3 tw:mt-4">
                    <div class="tw:bg-blue-50 tw:border tw:border-blue-100 tw:rounded-lg tw:p-3 tw:text-center">
                        <div class="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-blue-600 tw:mb-1">Aforo</div>
                        <div class="tw:text-2xl tw:font-bold tw:text-blue-800">{{ aforoTotal }}</div>
                    </div>
                    <div class="tw:bg-purple-50 tw:border tw:border-purple-100 tw:rounded-lg tw:p-3 tw:text-center">
                        <div class="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-purple-600 tw:mb-1">Bloqueado</div>
                        <div class="tw:text-2xl tw:font-bold tw:text-purple-800">{{ plazasBloqueadas }}</div>
                    </div>
                    <div class="tw:bg-emerald-50 tw:border tw:border-emerald-100 tw:rounded-lg tw:p-3 tw:text-center">
                        <div class="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-emerald-600 tw:mb-1">A la venta</div>
                        <div class="tw:text-2xl tw:font-bold tw:text-emerald-800">{{ plazasLibres }}</div>
                    </div>
                </div>
            </div>

            <!-- Form -->
            <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-200 tw:p-4 tw:space-y-4">
                <BaseInputText
                    :value="concepto"
                    @input="setConcepto"
                    label="Concepto"
                />
                <EtiquetaSelect @seleccionada="onEtiqueta" />
                <Button
                    class="tw:w-full"
                    @click="handleBloquear"
                    :disabled="plazasSeleccionadas.length === 0 || bloqueando"
                >
                    Bloquear / Liberar
                </Button>
                <LoadingSpinner v-if="bloqueando" />
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted, onUnmounted, getCurrentInstance, nextTick } from 'vue'
    import store from '@/store'
    import SvgPanZoom from 'vue-svg-pan-zoom'
    import LeafletComponent from '@/components/svg/components/LeafletComponent.vue'
    import SeatLegend from '@/modules/admin/ui/SeatLegend.vue'
    import LoadingSpinner from '@/modules/admin/ui/LoadingSpinner.vue'
    import EtiquetaSelect from '@/modules/admin/etiquetas/components/EtiquetaSelect.vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue'
    import { useSvgRenderer } from '@/composables/admin/abonos/useSvgRenderer'
    import { useSeatSelection } from '@/composables/admin/abonos/useSeatSelection'
    import { useBlockSeats } from '@/composables/admin/abonos/useBlockSeats'

    const props = defineProps({
        zona: { type: Object, required: true }
    })

    const instance = getCurrentInstance()
    const config = instance?.proxy?.config ?? store.getters.getConfig

    const plazas = ref(null)
    const svg_loaded = ref('')
    const load_panzoom = ref(false)
    const svgpanzoom = ref(null)
    const isPanning = ref(false)
    const svgListeners = []

    const { svgRendered, loading, originalColor, renderLeafletSvg, colors: svgColors } = useSvgRenderer()
    const { plazasSeleccionadas, asientos, clickButaca, selectAllButacas, clearAllButacas } = useSeatSelection(plazas)
    const { bloqueando, concepto, etiqueta, bloquearAsientos } = useBlockSeats()

    // --- Handlers for floating label inputs ---

    function setConcepto(val) {
        concepto.value = val
    }

    // --- Computed ---

    const aforoTotal = computed(() => {
        return plazas.value ? plazas.value.length : 0
    })

    const plazasBloqueadas = computed(() => {
        if (!plazas.value) return 0
        return plazas.value.filter(plaza => plaza.ocupada && plaza.ocupada.bloqueada).length
    })

    const plazasLibres = computed(() => {
        if (!plazas.value) return 0
        return plazas.value.filter(plaza => plaza.libre).length
    })

    const legendItems = computed(() => [
        { color: svgColors.libre, label: 'Libre' },
        { color: svgColors.seleccionada, label: 'Seleccionada' },
        { color: svgColors.bloqueada, label: 'Bloqueada' },
        { color: svgColors.vendidaAbonos, label: 'Vendida (abonos)' },
        { color: svgColors.vendidaEventos, label: 'Vendida (eventos)' },
        { color: svgColors.bloqueadaEventos, label: 'Bloqueada (eventos)' },
        { color: svgColors.enSesion, label: 'En sesión (abonos)' },
        { color: svgColors.enSesionEventos, label: 'En sesión (eventos)' },
        { color: svgColors.abonadoNoRenovado, label: 'Abonado no renovado' },
    ])

    // --- SVG handlers (tipo_svg == 1) ---

    function registerSvgPanZoom(svgpz) {
        svgpanzoom.value = svgpz
        load_panzoom.value = true
    }

    function zoomIn() {
        if (svgpanzoom.value) svgpanzoom.value.zoomIn()
    }

    function zoomOut() {
        if (svgpanzoom.value) svgpanzoom.value.zoomOut()
    }

    async function loadSVG(svg) {
        svg_loaded.value = svg
        await nextTick()

        const plazasElements = document.getElementsByClassName('recinto-zona')
        for (let i = 0; i < plazasElements.length; i++) {
            const el = plazasElements[i]
            if (el.dataset !== undefined) {
                const plaza_id = el.dataset.region
                const plaza = plazas.value ? plazas.value.find(p => p.id == plaza_id) : null
                if (plaza) {
                    if (!plaza.esta_disponible) {
                        el.classList.add('plaza_ocupada')
                    }
                    if (plaza.esta_bloqueada) {
                        el.classList.remove('plaza_ocupada')
                        el.classList.add('plaza_bloqueada')
                    }
                }
                const clickHandler = function() {
                    clickRegion(this.dataset.region, el)
                }
                el.addEventListener('click', clickHandler, false)
                svgListeners.push({ element: el, handler: clickHandler, eventType: 'click' })
            }
        }

        svgRendered.value = true
    }

    function clickRegion(plaza_id, plaza_svg = null) {
        const plaza = plazas.value ? plazas.value.find(p => p.id == plaza_id) : null
        if (plaza) {
            if (plaza.esta_bloqueada && plaza_svg) {
                plaza_svg.classList.toggle('plaza_bloqueada')
            }
            clickButaca(plaza)
        }
    }

    // --- Etiqueta handler ---

    function onEtiqueta(etiquetaSeleccionada) {
        etiqueta.value = etiquetaSeleccionada
    }

    // --- Bloquear handler ---

    function handleBloquear() {
        bloquearAsientos(asientos, () => {
            loadData()
            clearAllButacas()
        })
    }

    // --- Load data ---

    async function loadData() {
        loading.value = true
        svgRendered.value = false

        try {
            const res = await store.dispatch(config.getVuexAlias('back.abonos.listado-butacas-region'), {
                filters: {},
                lista_id: store.getters.getListaId,
                recinto_id: store.getters.getRecinto.id,
                region_id: props.zona.id
            })
            plazas.value = res.data.data

            const svgRes = await store.dispatch(config.getVuexAlias('recinto.zona-svg'), {
                region_id: props.zona.id,
                recinto_id: store.getters.getRecinto.id
            })

            if (props.zona.tipo_svg == 1) {
                await loadSVG(svgRes.data.svg)
            } else {
                renderLeafletSvg(svgRes.data.svg, plazas.value, {
                    onPlazaClick: clickButaca,
                    onPlazaVentaClick: plaza => {
                        if (plaza.info_ocupada?.id_abonado) {
                            const listaId = store.getters.getListaId
                            const url = `/admin/abonados/${listaId}/${plaza.info_ocupada.id_abonado}`
                            window.open(url, '_blank')
                        }
                    }
                })
            }
        } catch (e) {
            console.error('Error al cargar datos de la zona anexa:', e)
        } finally {
            loading.value = false
        }
    }

    onMounted(() => {
        loadData()
    })

    function disposeSvgListeners() {
        svgListeners.forEach(({ element, handler, eventType }) => {
            element.removeEventListener(eventType, handler)
        })
        svgListeners.length = 0
    }

    onUnmounted(() => {
        disposeSvgListeners()
    })
</script>

<style scoped>
.plaza_ocupada {
    fill: gray;
    opacity: 0.8;
}
.plaza_bloqueada {
    fill: #BF95C4;
    opacity: 1;
}
</style>
